/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Query } from 'react-apollo';

import gql from 'graphql-tag';
import Uri from 'jsuri';
import Q from 'q';
import { compose } from 'recompose';

import API from 'js/lib/api';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'js/l... Remove this comment to see the full error message
import getPropsFromPromise from 'js/lib/getPropsFromPromise';
import user from 'js/lib/user';
import waitFor from 'js/lib/waitFor';

import { InlineNotification } from '@coursera/cds-core';
import type {
  CreateBoxViewAnnotatorProfileRequest,
  CreateBoxViewAnnotatorProfileResponse,
  FindBoxViewAnnotatorProfileByUserIdRequest,
  FindBoxViewAnnotatorProfileByUserIdResponse,
} from '@coursera/grpc-types-boxintegration/coursera/proto/boxintegration/boxviewannotatorprofiles/v1beta1/box_view_annotator_profiles_api';

import CenteredLoadingSpinner from 'bundles/assess-common/components/CenteredLoadingSpinner';
import type {
  OnDemandBoxViewAnnotationSessionsV1,
  OnDemandBoxViewAnnotationSessionsV1CreateForGradingMutationVariables,
} from 'bundles/naptimejs/resources/__generated__/OnDemandBoxViewAnnotationSessionsV1';
import WidgetFrame from 'bundles/widget/components/cds/WidgetFrame';

import _t from 'i18n!nls/compound-assessments';

/* eslint-disable graphql/template-strings */
export const onDemandBoxViewAnnotationSessionsQuery = gql`
  query onDemandBoxViewAnnotationSessionsQuery(
    $body: String!
    $itemId: String!
    $reviewResponseId: String!
    $courseId: String!
    $userId: Number!
    $action: String!
    $annotatorProfileId: String!
  ) {
    onDemandBoxViewAnnotationSessions(
      body: $body
      itemId: $itemId
      reviewResponseId: $reviewResponseId
      courseId: $courseId
      userId: $userId
      action: $action
      annotatorProfileId: $annotatorProfileId
    )
      @rest(
        type: "RestOnDemandBoxViewAnnotationSessionsV1"
        path: "onDemandBoxViewAnnotationSessions.v1?action={args.action}&userId={args.userId}&courseId={args.courseId}&reviewResponseId={args.reviewResponseId}&itemId={args.itemId}{args.annotatorProfileId}"
        method: "POST"
        bodyKey: "body"
      ) {
      session @type(name: "RestOnDemandBoxViewAnnotationSessionsSession")
      sessionId
    }
  }
`;

type PropsFromParent = {
  itemId: string;
  courseId: string;
  userId: number;
  reviewResponseId: string;
  annotationsDisabled: boolean;
  isGraderView: boolean;
};

type PropsWithAnnotatorProfileId = {
  annotatorProfileId?: string;
};

type Props = PropsFromParent & PropsWithAnnotatorProfileId;

type OnDemandBoxViewAnnotationSessionsV1QueryResult = {
  onDemandBoxViewAnnotationSessions: OnDemandBoxViewAnnotationSessionsV1;
};

type OnDemandBoxViewAnnotationSessionsV1QueryVariables =
  OnDemandBoxViewAnnotationSessionsV1CreateForGradingMutationVariables & {
    action: string;
    body: {};
  };

const styles = {
  container: () => css`
    height: 100%;
  `,
};

export const InlineSubmissionTool: React.FC<Props> = ({
  itemId,
  courseId,
  reviewResponseId,
  userId,
  annotatorProfileId, // should only be available if annotationsDisabled = true
  isGraderView,
}) => (
  <div className="rc-InlineSubmissionTool" css={styles.container}>
    <Query<OnDemandBoxViewAnnotationSessionsV1QueryResult, OnDemandBoxViewAnnotationSessionsV1QueryVariables>
      query={onDemandBoxViewAnnotationSessionsQuery}
      variables={{
        body: {},
        itemId,
        courseId,
        reviewResponseId,
        userId: userId.toString(),
        // The BE implementation of these 2 actions use different auth methods and should be seen as:
        // - createForGrading: for graders only
        // - createForViewing: for learners only
        // Both methods can technically allow for annotations, but the main difference is the grader
        // version creates a widget session for the learner's submission, with the learner's userId.
        // Whereas the learner version creates a widget session for the user logged in, i.e. the learner
        action: isGraderView ? 'createForGrading' : 'createForViewing',
        // annotations are enabled when a valid annotatorProfileId is provided
        annotatorProfileId: annotatorProfileId ? `&annotatorProfileId=${annotatorProfileId}` : '',
      }}
    >
      {({ loading, error, data }) => {
        if (loading) {
          return <CenteredLoadingSpinner />;
        }

        if (error || !data || !data.onDemandBoxViewAnnotationSessions) {
          return (
            <InlineNotification severity="error" title={_t('Error')}>
              {_t('Failed to retrieve file.')}
            </InlineNotification>
          );
        }

        const { session, sessionId } = data.onDemandBoxViewAnnotationSessions;
        return (
          <WidgetFrame
            session={session}
            sessionId={sessionId}
            widgetId={`box-inline-feedback-${reviewResponseId}`}
            // aligns with in-frame boxview actions in expanded state
            modalHeaderCss={{ paddingRight: '20px' }}
            showPopupButton={true}
          />
        );
      }}
    </Query>
  </div>
);

export default compose<Props, PropsFromParent>(
  getPropsFromPromise(({ annotationsDisabled }: PropsFromParent): Q.Promise<PropsWithAnnotatorProfileId> => {
    if (annotationsDisabled) {
      return Q({});
    }

    // Attempt to get annotator profile for current user. Create one if it doesn't exist.
    const api = API('/api/grpc/boxintegration/boxviewannotatorprofiles/v1beta1/BoxViewAnnotatorProfilesAPI', {
      type: 'rest',
    });

    const fetchProfilesUri = new Uri('FindBoxViewAnnotatorProfileByUserId');
    const fetchData: FindBoxViewAnnotatorProfileByUserIdRequest = { userId: user.get().id };

    return Q(api.post(fetchProfilesUri.toString(), { data: fetchData })).then(
      (response: FindBoxViewAnnotatorProfileByUserIdResponse) => {
        if (response.boxViewAnnotatorProfile.length > 0) {
          return {
            annotatorProfileId: response.boxViewAnnotatorProfile[0].boxViewProfileAnnotatorId,
          };
        } else {
          const createProfileUri = new Uri('CreateBoxViewAnnotatorProfile');
          const createData: CreateBoxViewAnnotatorProfileRequest = {
            userId: user.get().id,
            displayName: user.get().display_name,
          };
          return Q(api.post(createProfileUri.toString(), { data: createData })).then(
            (createResponse: CreateBoxViewAnnotatorProfileResponse) => {
              return {
                annotatorProfileId: createResponse.boxViewAnnotatorProfile?.boxViewProfileAnnotatorId,
              };
            }
          );
        }
      }
    );
  }),
  waitFor((props) => props.annotationsDisabled || !!props.annotatorProfileId)
)(InlineSubmissionTool);
